import { useCallback } from 'react';

/**
 * DS RadioButton primitive.
 * Figma: Design-System AIMS-OS node 850:9469.
 *
 * Sizes (hit-area → visual ring):
 *   s → 16px hit / 12px ring   (Figma S)
 *   m → 24px hit / 18px ring   (Figma M)
 *   l → 32px hit / 24px ring   (Figma L)
 *
 * Colors follow the Checkbox token set:
 *   Unselected idle:      border `#5C5C5C`
 *   Selected:             border + dot `#2173FF`
 *   Disabled unselected:  border `#BABABA`
 *   Disabled selected:    border + dot `#80AFFF`
 *
 * Accessibility: `role="radio"` + `aria-checked`. Use inside a `role="radiogroup"` wrapper.
 *
 * @param {object} props
 * @param {boolean}  [props.checked=false]
 * @param {boolean}  [props.disabled=false]
 * @param {'s'|'m'|'l'} [props.size='m']
 * @param {() => void} [props.onChange] — called when the user activates the button (click / Space / Enter)
 * @param {string}   [props.className]
 * @param {string}   [props.id]
 * @param {string}   [props['aria-label']]
 */

/** Outer hit-area size (padding zone). */
const HIT_SIZE = { s: 'h-4 w-4', m: 'h-6 w-6', l: 'h-8 w-8' };

/**
 * Padding per size so the ring fits exactly:
 *   s: 16px - 2×2px = 12px ring  ✓
 *   m: 24px - 2×3px = 18px ring  ✓
 *   l: 32px - 2×4px = 24px ring  ✓
 */
const HIT_PADDING = { s: 'p-0.5', m: 'p-[3px]', l: 'p-1' };

/** Visual ring size. */
const RING_SIZE = { s: 'h-3 w-3', m: 'h-[18px] w-[18px]', l: 'h-6 w-6' };

/** Inner dot size (selected). About 50% of the ring. */
const DOT_SIZE = { s: 'h-1.5 w-1.5', m: 'h-[9px] w-[9px]', l: 'h-3 w-3' };

function normalizeSize(size) {
  if (size === 's' || size === 'm' || size === 'l') return size;
  return 'm';
}

const RadioButton = ({
  checked = false,
  disabled = false,
  size = 'm',
  onChange,
  className = '',
  id,
  'aria-label': ariaLabel,
}) => {
  const s = normalizeSize(size);

  const handleClick = useCallback(
    (e) => {
      if (disabled || checked) return; // radio: can't deselect by clicking again
      onChange?.(e);
    },
    [disabled, checked, onChange],
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (disabled) return;
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        if (!checked) onChange?.(e);
      }
    },
    [disabled, checked, onChange],
  );

  const hitStateClass = disabled
    ? ''
    : checked
      ? 'hover:bg-[#E9F1FF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#80AFFF] focus-visible:ring-offset-2 dark:hover:bg-blue-950/40 dark:focus-visible:ring-blue-400'
      : 'hover:bg-[#F2F2F2] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 dark:hover:bg-white/10 dark:focus-visible:ring-gray-500';

  let ringClass;
  if (disabled) {
    ringClass = checked
      ? 'border-2 border-[#80AFFF]'
      : 'border-2 border-[#BABABA]';
  } else {
    ringClass = checked
      ? 'border-2 border-[#2173FF] dark:border-blue-400'
      : 'border-2 border-[#5C5C5C] dark:border-gray-400';
  }

  const dotClass = disabled
    ? 'bg-[#80AFFF]'
    : 'bg-[#2173FF] dark:bg-blue-400';

  return (
    <button
      id={id}
      type="button"
      role="radio"
      aria-checked={checked}
      aria-disabled={disabled}
      aria-label={ariaLabel}
      disabled={disabled}
      className={[
        'inline-flex shrink-0 items-center justify-center rounded-full border-none transition-colors duration-200',
        HIT_SIZE[s],
        HIT_PADDING[s],
        hitStateClass,
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {/*
       * Visual ring — `relative` so the dot can be absolutely centered.
       * The dot uses `absolute` to stay OUT of the flex flow and never
       * affect the ring's size or baseline, preventing vertical shifts.
       */}
      <span
        className={[
          'relative shrink-0 rounded-full bg-surface transition-colors',
          RING_SIZE[s],
          ringClass,
        ].join(' ')}
      >
        {checked && (
          <span
            className={[
              'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full',
              DOT_SIZE[s],
              dotClass,
            ].join(' ')}
            aria-hidden
          />
        )}
      </span>
    </button>
  );
};

export default RadioButton;
